import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

import { Icon as DropdownIcon } from '@components';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import ApiMethodChip from '@features/docs/components/api-method-chip/ApiMethodChip';

import { Item, SidebarItemProps } from './types';

const SidebarItem = (props: SidebarItemProps) => {
  const location = useLocation();

  const { item, path, isOrgLevelItem, branchId, isProfileList, isNestedItem } =
    props;

  const { id, Icon, label, route, children, method } = item;

  // Helper to check if current route matches this item or any of its descendants
  const isRouteActive = (sidebarItem: Item, currentPath: string): boolean => {
    if (currentPath.startsWith(sidebarItem.route)) {
      return true;
    }
    if (sidebarItem.children && sidebarItem.children.length > 0) {
      return sidebarItem.children.some((child) =>
        isRouteActive(child, currentPath)
      );
    }
    return false;
  };

  // Use the recursive check for current route
  const isCurrentRoute = isRouteActive(item, location.pathname + location.hash);

  const [isExpanded, setIsExpanded] = useState(isCurrentRoute);

  const itemBgColor = isCurrentRoute ? 'bg-primaryLite9' : 'hover:bg-grey10';
  // eslint-disable-next-line no-nested-ternary
  const itemIconColor = isCurrentRoute
    ? 'text-primary'
    : isProfileList
    ? 'text-grey4'
    : 'text-bgGrey';
  const itemTextColor = isCurrentRoute ? 'text-primary' : 'text-bgGrey';

  const sideBarClick = () => {
    sendMixpanelEvent(Event.SIDEBAR_TAB_CLICKED, {
      section: label,
      branchId
    });

    if (children) {
      setIsExpanded(!isExpanded);
    }
  };

  const renderNestedList = (child: Item) => {
    const { id: childId, route: childRoute } = child;

    return (
      <SidebarItem
        key={childId}
        item={child}
        path={childRoute}
        isOrgLevelItem
        branchId={branchId}
        isProfileList={isProfileList}
        isNestedItem
      />
    );
  };

  return (
    <div>
      <Link
        key={id}
        to={path || route}
        className={
          isOrgLevelItem
            ? `flex items-center justify-between pl-1 pr-2 ${
                isProfileList && isCurrentRoute && !isNestedItem
                  ? 'bg-grey1'
                  : ''
              }  ${isProfileList ? 'rounded-lg hover:bg-grey1' : ''}`
            : `flex items-center gap-3 rounded-lg px-4 py-3 ${itemBgColor}`
        }
        onClick={sideBarClick}
      >
        <div
          className={`flex items-center gap-3 ${
            isProfileList ? 'mt-0.5 h-9' : ''
          } ${isNestedItem ? '!h-7' : ''}`}
        >
          {Icon && (
            <Icon
              className={clsx(
                isProfileList ? 'size-4.5' : 'size-6',
                itemIconColor
              )}
            />
          )}
          {method && <ApiMethodChip method={method} />}
          <span
            className={`break-words text-sm font-medium ${
              isProfileList && !isNestedItem ? 'text-darkGrey' : itemTextColor
            }`}
          >
            {label}
          </span>
        </div>
        {(children?.length ?? 0) > 0 && (
          <span className="text-bgGrey">
            <DropdownIcon
              name="down-icon-dark"
              size="16"
              className={clsx(
                'transition-transform duration-300 ease-in-out',
                isExpanded ? 'rotate-0' : '-rotate-90'
              )}
            />
          </span>
        )}
      </Link>

      <div
        className={clsx(
          'ml-7.5 flex flex-col overflow-hidden transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.98,0.16,0.2,1)]',
          isExpanded ? 'max-h-96 opacity-100' : 'pointer-events-none max-h-0'
        )}
      >
        {children?.length &&
          children.length > 0 &&
          children.map((child) => renderNestedList(child))}
      </div>
    </div>
  );
};

export default SidebarItem;
